import { useIntl, uuidPattern } from '@capsicum/common';
import { useMemo, useState } from 'react';

import { useCategories } from './useCategories';
import { useProducts } from './useProducts';

type ProductsLobbyProps = {
	parentCategoryId?: string;
	partnerIds?: Array<string>;
	selectedMaxChips?: number;
	subcategoryIds: Array<string> | string | undefined;
	withAllSubcategories?: boolean;
};

export const useProductsLobby = ({
	parentCategoryId,
	partnerIds,
	selectedMaxChips = 0,
	subcategoryIds,
	withAllSubcategories
}: ProductsLobbyProps) => {
	const {
		getCategoryBy,
		getProductAllSubcategories,
		getProductCategoriesBy,
		isLoading: isCategoriesLoading
	} = useCategories(partnerIds);
	const { getLocalizedField } = useIntl();
	const subcategoriesChips = withAllSubcategories
		? getProductAllSubcategories()
		: getProductCategoriesBy(c => {
				return c?.parentCategory?.id === parentCategoryId;
			});
	const getCategorySubcategories = subcategoriesChips?.map(sub => sub.id!);

	// Prevent polluting BE with non uuid parameters
	const filteredCategoryIds = subcategoryIds?.length
		? [...subcategoryIds].filter(el => uuidPattern.test(el) || el === 'all')
		: [];

	const [chosenSubcategoryIds, setChosenSubcategoryIds] = useState<Array<string>>(
		filteredCategoryIds?.length ? [...filteredCategoryIds] : getCategorySubcategories
	);

	const {
		fetchNextPage,
		hasNextPage,
		isFetched,
		isFetching,
		isFetchingNextPage,
		loadingProducts,
		products,
		refetchProducts,
		remove
	} = useProducts({
		categoryIds: chosenSubcategoryIds?.[0] === 'all' ? getCategorySubcategories : chosenSubcategoryIds,
		partnerIds
	});

	const chipCategories = useMemo(
		() =>
			subcategoriesChips?.map(subcategory => {
				const isSelected = chosenSubcategoryIds.some(id => id === subcategory.id);
				const isDisabled =
					selectedMaxChips > 0 &&
					chosenSubcategoryIds[0] !== 'all' &&
					chosenSubcategoryIds.length >= selectedMaxChips &&
					!isSelected;

				return {
					description: subcategory.id || '',
					name: getLocalizedField('name', subcategory),
					selected: chosenSubcategoryIds.some(id => id === subcategory.id),
					status: isDisabled ? 'disabled' : subcategory.status || 'enabled',
					value: subcategory.id || ''
				};
			}),
		[chosenSubcategoryIds, subcategoriesChips, selectedMaxChips]
	);

	const categoryTitle = useMemo(() => {
		const parentCategory = getCategoryBy(c => {
			return c?.parentCategory?.id === parentCategoryId;
		})?.parentCategory;

		return getLocalizedField('name', parentCategory || {});
	}, [getCategoryBy, getLocalizedField, parentCategoryId]);

	return {
		categoryTitle,
		chipCategories,
		chosenSubcategoryIds,
		fetchNextPage,
		hasNextPage,
		isCategoriesLoading,
		isFetched,
		isFetching,
		isFetchingNextPage,
		loadingProducts,
		products,
		refetchProducts,
		remove,
		setChosenSubcategoryIds
	};
};
